import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CareerFocusWithMetrics } from '../../../hooks/useCareerFocus';
import { InternshipResult } from '../../../types/internship';
import { useDarkMode } from '../../../context/DarkModeContext';
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  ArrowRightOnRectangleIcon,
  DocumentTextIcon,
  CalendarIcon,
  MapPinIcon,
  UserGroupIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { formatDateOnly } from '../../../utils/dateUtils';
import OutcomeWizard from './OutcomeWizard';
import DeleteConfirmModal from './DeleteConfirmModal';
import { useInternshipResults } from '../../../hooks/useInternshipResults';
import { useToast } from '../../../context/ToastContext';

interface ResultsSummaryProps {
  result: InternshipResult;
  focus: CareerFocusWithMetrics;
  onRefresh?: () => Promise<void>;
}

const ResultsSummary: React.FC<ResultsSummaryProps> = ({ result, focus, onRefresh }) => {
  const { darkMode } = useDarkMode();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { deleteResult } = useInternshipResults(focus.id);
  const [showEditWizard, setShowEditWizard] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const bgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-900';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-600';
  const successColor = darkMode ? 'text-green-400' : 'text-green-600';
  const dangerColor = darkMode ? 'text-red-400' : 'text-red-600';
  const warningColor = darkMode ? 'text-amber-400' : 'text-amber-600';
  
  const getOutcomeIcon = () => {
    switch (result.outcomeType) {
      case 'offer_received':
        return <CheckCircleIcon className={`w-6 h-6 ${successColor}`} />;
      case 'no_offer':
        return <XCircleIcon className={`w-6 h-6 ${dangerColor}`} />;
      case 'withdrew':
        return <ArrowRightOnRectangleIcon className={`w-6 h-6 ${warningColor}`} />;
      default:
        return null;
    }
  };
  
  const getOutcomeText = () => {
    switch (result.outcomeType) {
      case 'offer_received':
        return 'Offer Received';
      case 'no_offer':
        return 'Did Not Receive Offer';
      case 'withdrew':
        return 'Withdrew from Process';
      default:
        return 'Pending';
    }
  };
  
  const getDecisionBadge = () => {
    if (result.outcomeType !== 'offer_received' || !result.decisionStatus) return null;
    
    const badgeColors = {
      accepted: darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800',
      declined: darkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800',
      pending: darkMode ? 'bg-amber-900 text-amber-300' : 'bg-amber-100 text-amber-800'
    };
    
    const badgeText = {
      accepted: 'Accepted',
      declined: 'Declined',
      pending: 'Decision Pending'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badgeColors[result.decisionStatus]}`}>
        {badgeText[result.decisionStatus]}
      </span>
    );
  };

  const handleEdit = () => {
    setShowEditWizard(true);
  };
  
  const handleWizardComplete = async () => {
    setShowEditWizard(false);
    // Refresh the results data without page reload
    if (onRefresh) {
      await onRefresh();
    }
  };
  
  const handleWizardCancel = () => {
    setShowEditWizard(false);
  };
  
  const handleDelete = async (deleteFiles: boolean) => {
    try {
      await deleteResult(result.id, deleteFiles);
      showToast(
        `Result deleted successfully${deleteFiles ? ' along with documents' : ''}`, 
        'success'
      );
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error) {
      showToast('Failed to delete result', 'error');
      console.error('Error deleting result:', error);
    }
  };
  
  // Show wizard if editing
  if (showEditWizard) {
    return (
      <OutcomeWizard 
        focus={focus} 
        existingResult={result}
        onComplete={handleWizardComplete}
        onCancel={handleWizardCancel}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Outcome Summary */}
      <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            {getOutcomeIcon()}
            <div>
              <h3 className={`text-lg font-semibold ${textColor}`}>
                {getOutcomeText()}
              </h3>
              <p className={`text-sm ${mutedTextColor}`}>
                Recorded on {formatDateOnly(result.outcomeRecordedAt)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleEdit}
              className={`p-2 rounded-lg transition-colors ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Edit Result"
            >
              <PencilIcon className={`w-5 h-5 ${mutedTextColor}`} />
            </button>
            <button
              onClick={() => setShowDeleteModal(true)}
              className={`p-2 rounded-lg transition-colors ${
                darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
              }`}
              title="Delete Result"
            >
              <TrashIcon className={`w-5 h-5 ${mutedTextColor} hover:text-red-500`} />
            </button>
          </div>
        </div>
        
        {/* Decision Timeline */}
        {result.outcomeType === 'offer_received' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${mutedTextColor}`}>Decision Status</span>
              {getDecisionBadge()}
            </div>
            
            {result.offerDetails && (
              <div className={`grid grid-cols-2 gap-4 pt-3 border-t ${borderColor}`}>
                {result.offerDetails.startDate && (
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    <div>
                      <p className={`text-xs ${mutedTextColor}`}>Start Date</p>
                      <p className={`text-sm font-medium ${textColor}`}>
                        {formatDateOnly(result.offerDetails.startDate)}
                      </p>
                    </div>
                  </div>
                )}
                
                {result.offerDetails.location && (
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="w-4 h-4" />
                    <div>
                      <p className={`text-xs ${mutedTextColor}`}>Location</p>
                      <p className={`text-sm font-medium ${textColor}`}>
                        {result.offerDetails.location}
                      </p>
                    </div>
                  </div>
                )}
                
                {result.offerDetails.team && (
                  <div className="flex items-center gap-2">
                    <UserGroupIcon className="w-4 h-4" />
                    <div>
                      <p className={`text-xs ${mutedTextColor}`}>Team</p>
                      <p className={`text-sm font-medium ${textColor}`}>
                        {result.offerDetails.team}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        
        {/* Feedback for no offer */}
        {result.outcomeType === 'no_offer' && result.feedbackReceived && (
          <div className={`mt-4 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
            <p className={`text-sm font-medium mb-2 ${textColor}`}>Feedback Received</p>
            <p className={`text-sm ${mutedTextColor}`}>{result.feedbackReceived}</p>
          </div>
        )}
      </div>
      
      {/* Goal Achievement */}
      {result.achievementsSummary && (
        <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${textColor}`}>
            Goal Achievement
          </h3>
          <div className={`prose prose-sm ${darkMode ? 'prose-invert' : ''} max-w-none`}>
            <p className={mutedTextColor}>{result.achievementsSummary}</p>
          </div>
        </div>
      )}
      
      {/* Key Learnings */}
      {result.lessonsLearned && (
        <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${textColor}`}>
            Key Learnings
          </h3>
          <div className={`prose prose-sm ${darkMode ? 'prose-invert' : ''} max-w-none`}>
            <p className={mutedTextColor}>{result.lessonsLearned}</p>
          </div>
        </div>
      )}
      
      {/* Documents */}
      {result.documents && result.documents.length > 0 && (
        <div className={`${bgColor} rounded-lg border ${borderColor} p-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${textColor}`}>
            Documents
          </h3>
          <div className="space-y-2">
            {result.documents.map((doc) => (
              <a
                key={doc.id}
                href={`/files?id=${doc.fileId}`}
                className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}
              >
                <DocumentTextIcon className="w-5 h-5" />
                <div className="flex-1">
                  <p className={`text-sm font-medium ${textColor}`}>
                    {doc.documentName}
                  </p>
                  <p className={`text-xs ${mutedTextColor}`}>
                    {doc.documentType.replace('_', ' ')}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
      
      {/* Completion Status */}
      {!result.isFinal && (
        <div className={`text-center py-4 px-6 rounded-lg ${
          darkMode ? 'bg-amber-900/20 text-amber-400' : 'bg-amber-50 text-amber-700'
        }`}>
          <p className="text-sm">
            Your results are saved but not finalized. You can continue editing or add more details.
          </p>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        result={result}
      />
    </div>
  );
};

export default ResultsSummary;